/**
 * Partner Portal Auth Store — Zustand store for partner portal authentication
 *
 * Mirrors the vendorAuthStore pattern. partner portal staff authenticate with
 * email + password (bcrypt JWT), not an API key.
 *
 * Key difference from vendor: 2FA is mandatory. The store tracks
 * whether the current session has completed 2FA verification so the
 * portal can enforce setup on first login.
 *
 * @version 1.0.0
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface PartnerPortalOrg {
  org_id: string;
  org_name: string;
  dba_name?: string | null;
  org_type: string;
  ein?: string | null;
  verification_status: 'pending' | 'verified' | 'suspended';
  verification_badges: string[];
  // Contact
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  primary_contact_title?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  hero_image_url?: string | null;
  // Compliance
  license_type?: string | null;
  license_number?: string | null;
  license_state?: string | null;
  license_expiry?: string | null;
  license_doc_url?: string | null;
  coi_insurer?: string | null;
  coi_amount?: number | null;
  coi_expiry?: string | null;
  coi_doc_url?: string | null;
  hipaa_baa_signed?: boolean;
  hipaa_baa_date?: string | null;
  background_check_policy?: string | null;
  nonprofit_doc_url?: string | null;
  // Public profile
  mission_statement?: string | null;
  year_established?: number | null;
  staff_count?: number | null;
  volunteer_count?: number | null;
  annual_families_served?: number | null;
  languages_served?: string[];
  service_areas?: unknown[];
  // Onboarding
  onboarding_step?: number;
  onboarding_complete?: boolean;
  public_profile_complete?: boolean;
  compliance_submitted?: boolean;
  listing_status?: string | null;
  claimed_network_name?: string | null;
  // Billing
  listing_fee_paid: boolean;
  fee_structure_selected?: string | null;
  stripe_connect_account_id?: string | null;
}

export interface PartnerPortalUser {
  user_id: string;
  email: string;
  display_name?: string | null;
  two_factor_enabled: boolean;
  /** True when an AgenticED superuser is impersonating this partner portal user. 2FA is bypassed. */
  is_superuser_session?: boolean;
  org: PartnerPortalOrg | null;
}

export interface PartnerPortalAuthState {
  /** Authenticated partner portal user */
  user: PartnerPortalUser | null;
  /** JWT bearer token */
  token: string | null;
  /** Whether 2FA has been verified in this session */
  twoFactorVerified: boolean;
  /** Whether user is fully authenticated (token + 2FA if required) */
  isAuthenticated: boolean;
  /** Session expiry ISO string */
  expiresAt: string | null;
  /** Auth timestamp */
  loggedInAt: number | null;
}

export interface PartnerPortalAuthActions {
  /** Set user after successful login (before 2FA check) */
  setUser: (user: PartnerPortalUser, token: string) => void;
  /** Mark 2FA as verified for this session */
  mark2FAVerified: () => void;
  /** Update org data (e.g. after fee payment) */
  updateOrg: (updates: Partial<PartnerPortalOrg>) => void;
  /** Clear state (logout) */
  logout: () => void;
  /** Check if session is expired */
  isExpired: () => boolean;
  /** Whether portal is accessible (authenticated + 2FA complete) */
  canAccessPortal: () => boolean;
}

// ─────────────────────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────────────────────

const initialState: PartnerPortalAuthState = {
  user: null,
  token: null,
  twoFactorVerified: false,
  isAuthenticated: false,
  expiresAt: null,
  loggedInAt: null,
};

// ─────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────

export const usePartnerPortalAuthStore = create<PartnerPortalAuthState & PartnerPortalAuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user: PartnerPortalUser, token: string) => {
        const now = Date.now();
        const expiresAt = new Date(now + 24 * 60 * 60 * 1000).toISOString();
        set({
          user,
          token,
          // If the user has already set up 2FA previously, mark as verified
          // (they'll still need to verify each session via the login 2FA step)
          twoFactorVerified: false,
          isAuthenticated: !user.two_factor_enabled, // no 2FA = immediately authenticated
          expiresAt,
          loggedInAt: now,
        });
      },

      mark2FAVerified: () => {
        set({ twoFactorVerified: true, isAuthenticated: true });
      },

      updateOrg: (updates: Partial<PartnerPortalOrg>) => {
        const { user } = get();
        if (!user?.org) return;
        set({ user: { ...user, org: { ...user.org, ...updates } } });
      },

      logout: () => {
        set(initialState);
      },

      isExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        return new Date(expiresAt) < new Date();
      },

      canAccessPortal: () => {
        const { isAuthenticated, user } = get();
        if (!isAuthenticated) return false;
        if (get().isExpired()) return false;
        // Superuser impersonation sessions bypass 2FA entirely
        if (user?.is_superuser_session) return true;
        // If user has 2FA enabled, twoFactorVerified must be true
        if (user?.two_factor_enabled && !get().twoFactorVerified) return false;
        return true;
      },
    }),
    {
      name: 'agenticed_partner_portal_auth',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// ─────────────────────────────────────────────────────────────
// HELPER EXPORTS
// ─────────────────────────────────────────────────────────────

export function isPartnerPortalAuthenticated(): boolean {
  return usePartnerPortalAuthStore.getState().canAccessPortal();
}

export function getPartnerPortalToken(): string | null {
  return usePartnerPortalAuthStore.getState().token;
}
